/**
 * A link that leaves an app's own browser (Facebook's, Instagram's, TikTok's) for the phone's real
 * one, with the idea and the visitor's id in it (easy-signup handoff §4, 2026-10-04).
 *
 *  - iOS: `x-safari-https://…`, which Safari answers from inside other apps (iOS 17 and later).
 *  - Android: an `intent://` link with no package named, so the phone's default browser opens it.
 *
 * Neither is promised by the apps that host these browsers, which is why the screen also says how
 * to do it by hand. The id keeps the journey one line across the two browsers (analytics/funnel.ts
 * adopts `vid` when the new browser has none of its own).
 */
import { currentVisit } from '../analytics/visits';

export function openInBrowserUrl(
  { os, idea }: { os: string; idea?: string },
  origin = typeof window !== 'undefined' ? window.location.origin : 'https://build.saddle-sites.com',
): string | null {
  if (os !== 'ios' && os !== 'android') return null;
  const params = new URLSearchParams();
  if (idea?.trim()) params.set('idea', idea.trim());
  let vid = '';
  try {
    vid = localStorage.getItem('saddle.visitor') ?? '';
  } catch {
    vid = '';
  }
  if (vid) params.set('vid', vid);
  // The ad that brought them, so the other browser's visit is still tied to it.
  const visit = currentVisit();
  if (visit.ref === 'ad') params.set('utm_source', 'fb');
  if (visit.utm_campaign) params.set('utm_campaign', visit.utm_campaign);
  if (visit.utm_content) params.set('utm_content', visit.utm_content);
  params.set('ref_browser', 'in_app');
  const target = new URL(`/new/idea?${params.toString()}`, origin);
  if (os === 'ios') return `x-safari-${target.toString()}`;
  return `intent://${target.host}${target.pathname}${target.search}#Intent;scheme=https;end`;
}
